import type { Role, SettingsSectionKey, DeployEnv } from '@aithora/shared';
import { mayOpenSettingsTab } from '@aithora/shared';

/**
 * The design's role lenses for Settings (settingsVals V5 L29229-29260). The console role
 * picks one; the server enforces the same matrix again (v3/settings/sections.ts).
 *
 *   Super Admin  the Operator lens with the destructive controls (Danger Zone, role changes)
 *   Operator     the Operator lens without them
 *   Partner      the Operator lens, read only (handoff 10g: backend-only, read-only everywhere)
 *   Sales, Dev, Facility Admin, Care Staff, Family, Aithora Home: their own section lists
 *
 * Residents never reach Settings (canSee), so the design's Resident list is not ported.
 */
export type Lens = 'Operator' | 'Sales' | 'Dev' | 'Facility Admin' | 'Care Staff' | 'Family' | 'Aithora Home';

export interface SettingsLens {
  lens: Lens;
  /** The design's aiTier: Super Admin carries the destructive controls. */
  superAdmin: boolean;
}

export function lensOf(role: Role | null): SettingsLens {
  switch (role) {
    case 'SUPER_ADMIN': return { lens: 'Operator', superAdmin: true };
    case 'OPERATOR':
    case 'PARTNER': return { lens: 'Operator', superAdmin: false };
    case 'SALES': return { lens: 'Sales', superAdmin: false };
    case 'DEVELOPER': return { lens: 'Dev', superAdmin: false };
    case 'ADMIN': return { lens: 'Facility Admin', superAdmin: false };
    case 'CAREGIVER':
    case 'STAFF': return { lens: 'Care Staff', superAdmin: false };
    case 'FAMILY_MEMBER': return { lens: 'Family', superAdmin: false };
    default: return { lens: 'Aithora Home', superAdmin: false };
  }
}

export type SectionName =
  | 'Organization' | 'Security & Keys' | 'Two-factor' | 'Guard & Response' | 'Notifications' | 'On-call' | 'Guardian rules'
  | 'Voice & Scribe' | 'Data & Retention' | 'Daily Schedule' | 'Compliance' | 'Models & Spend' | 'Users & Permissions'
  | 'Integrations' | 'Test Lab' | 'Tests' | 'Danger Zone' | 'Your preferences' | 'Who can see this' | 'What you can see'
  | 'My connections' | 'Atlas Assistant' | 'Alert ladder' | 'Break-glass' | 'Channels' | 'Pilot go-live' | 'Surveyor export'
  | 'Med pass';

/** The Operator's sections, in the design's order (SEC, Console V6), then Pilot go-live: one of
 *  the building sections the facility switcher opens for one building (V6 README, Settings),
 *  so Super Admin and Operator reach it too. The side list shows it under "Who we are". */
const OPERATOR_SEC: SectionName[] = ['Organization', 'Security & Keys', 'Break-glass', 'Two-factor', 'My connections', 'Channels', 'Guard & Response', 'Notifications', 'On-call', 'Guardian rules', 'Alert ladder', 'Voice & Scribe',
  'Data & Retention', 'Daily Schedule', 'Compliance', 'Models & Spend', 'Users & Permissions', 'Med pass', 'Integrations', 'Atlas Assistant', 'Test Lab', 'Tests', 'Pilot go-live'];

/** Every other lens's own list (CONSUMER_SEC, V4 L22947). */
const LENS_SEC: Record<Exclude<Lens, 'Operator'>, SectionName[]> = {
  Family: ['What you can see', 'My connections'],
  'Aithora Home': ['Your preferences', 'Who can see this'],
  'Facility Admin': ['Organization', 'Pilot go-live', 'Surveyor export', 'Users & Permissions', 'Med pass', 'Break-glass', 'Two-factor', 'My connections', 'Channels', 'Notifications', 'Guardian rules', 'Alert ladder', 'Voice & Scribe', 'Daily Schedule', 'Data & Retention', 'Integrations'],
  'Care Staff': ['Two-factor', 'My connections', 'Channels', 'Notifications'],
  Sales: ['Two-factor', 'My connections', 'Channels', 'Notifications'],
  Dev: ['Two-factor', 'My connections', 'Channels', 'Notifications', 'Atlas Assistant', 'Integrations', 'Security & Keys', 'Test Lab', 'Tests'],
};

export function sectionsOf(lens: Lens): SectionName[] {
  return lens === 'Operator' ? OPERATOR_SEC : LENS_SEC[lens];
}

/** The Partner's own list (V5 L29253): account only. Users & Permissions, governance and the treasury are hidden (Andrew, 2026-10-02). */
const PARTNER_SEC: SectionName[] = ['Two-factor', 'My connections', 'Channels', 'Notifications'];

/**
 * The sections a role sees: its lens's list, less what the server refuses that role. The
 * Partner reads the Operator lens elsewhere but sees only its own account sections here.
 * Atlas Assistant follows the shared rule the server enforces (settings-access.ts): Super
 * Admin and Operator everywhere, a Developer only outside production. `env` comes from the
 * server (GET /settings/sections); until it is known it is production, the stricter answer.
 */
export function sectionsForRole(role: Role | null, env: DeployEnv = 'production'): SectionName[] {
  const list = role === 'PARTNER' ? PARTNER_SEC : sectionsOf(lensOf(role).lens);
  return list.filter((n) => n !== 'Atlas Assistant' || mayOpenSettingsTab(role, 'Atlas Assistant', env));
}

/** The side list's groups (GROUPS, V4 L23202). A section in none of them goes under "You". */
export const GROUPS: [string, SectionName[]][] = [
  ['Who we are', ['Organization', 'Pilot go-live']],
  ['Who can do what', ['Users & Permissions', 'Med pass', 'Break-glass', 'Two-factor', 'My connections', 'Channels', 'Security & Keys']],
  ['What we do with data', ['Data & Retention', 'Voice & Scribe', 'Compliance', 'Guard & Response', 'Models & Spend']],
  ['Who gets told', ['Surveyor export', 'Notifications', 'On-call', 'Guardian rules', 'Alert ladder', 'Daily Schedule']],
  ['How we connect', ['Integrations']],
  ['Aithora only', ['Atlas Assistant', 'Test Lab', 'Tests']],
];

/** The Family lens lists its sections without group headings (grpOf, V4 L23227). */
export const groupsFor = (lens: Lens): [string, SectionName[]][] => (lens === 'Family' ? [] : GROUPS);

/** Search keywords per section (KW, V4 L23205). */
export const KEYWORDS: Record<SectionName, string> = {
  Organization: 'name legal tenant organization id plan tier address street region billing baa signed npi ccn identity',
  'Users & Permissions': 'team members roles access requests invite add people bulk multi-sig approver rbac user',
  'Two-factor': 'passkey authenticator totp backup codes trusted devices mfa sign-in 2fa',
  'Alert ladder': 'alert ladder escalation page paging steps shift critical high medium 911 re-page test page channels voice sms',
  'Security & Keys': 'api key programmatic revoke rotate create posture live key',
  'Guard & Response': 'guard state response level',
  Notifications: 'quiet hours digest channel alerts email sms abuse neglect escalation routing',
  'Data & Retention': 'retention ledger audit export cadence on-demand',
  'Daily Schedule': 'schedule shift routine day',
  'On-call': 'on-call pager paging escalation rotation twilio text phone call regional lead backup',
  'Guardian rules': 'guardian owner incident fall abuse missing medication deadline report escalate unclaimed',
  'Voice & Scribe': 'voice siri google alexa scribe recording consent audio transcript note',
  Compliance: 'eu ai act regulatory frameworks decision records high-risk override rate',
  'Models & Spend': 'model routing enforcement provider spend usage',
  Integrations: 'connections catalog ehr',
  'Test Lab': 'scenarios message intent companion risk signal channel guard fired',
  Tests: 'test suite passed failed run',
  'Your preferences': 'text size first screen preferences',
  'Who can see this': 'family sharing visibility',
  'What you can see': 'family access',
  'Danger Zone': '',
  'Atlas Assistant': 'assistant atlas model claude gemma mistral shadow chat confirmation yes team texts whatsapp autonomy pin tries caps own model',
  'My connections': 'phone number text sms test text pin stop start opt-in consent linear key gmail calendar drive atlas by text',
  'Break-glass': 'break-glass break glass emergency access aithora staff resident records request approve deny log',
  'Pilot go-live': 'pilot go live launch checklist ready readiness',
  'Surveyor export': 'surveyor survey state inspection export timeline ledger audit date range pdf csv secure link expires revoke',
  'Med pass': 'med pass medication medicine emar bcma barcode scan scanning strict mode skip reason wristband band package rx number ndc pharmacy hold rule vital pulse',
  Channels: 'channels text sms whatsapp push notifications in-app voice call consent quiet hours fallback how atlas reaches me',
};

/** The server's key for each section (history, the role matrix). Null: the server has none. */
export const SECTION_KEY: Record<SectionName, SettingsSectionKey | null> = {
  Organization: 'organization',
  'Security & Keys': 'security_keys',
  'Two-factor': 'two_factor',
  'Guard & Response': 'guard_response',
  Notifications: 'notifications',
  'On-call': 'oncall',
  'Guardian rules': 'guardian_rules',
  'Voice & Scribe': 'voice_scribe',
  'Data & Retention': 'data_retention',
  'Daily Schedule': 'schedule',
  Compliance: 'compliance',
  'Models & Spend': 'models_spend',
  'Users & Permissions': 'users',
  Integrations: 'integrations',
  'Test Lab': 'test_lab',
  Tests: 'tests',
  'Danger Zone': 'danger_zone',
  'Your preferences': null,
  'Who can see this': null,
  'What you can see': null,
  'My connections': null,
  'Atlas Assistant': null,
  'Alert ladder': null,
  'Break-glass': null,
  Channels: null,
  'Pilot go-live': null,
  'Surveyor export': null,
  'Med pass': 'med_pass',
};

export const groupOf = (n: SectionName, lens?: Lens): string => (lens === 'Family' ? 'You' : GROUPS.find((g) => g[1].includes(n))?.[0] ?? 'You');

export const SECTION_SLUG = (n: SectionName) => n.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
